import { useQuery } from '@tanstack/react-query';
import api from '@/Services/api';
import { API } from '@/Services/apiConfig';
import { AvailabilityDay, ResolvedSlot, FoodUpdate } from '@/types/menu';

function buildFallback(date: string | undefined): MenuSlotResponse {
    return {
        slot: date
            ? { date, period: 'lunch', fulfilment: 'pickup', slotWeight: 0 }
            : null,
        foodUpdates: new Map<number, FoodUpdate>(),
        availableMenuIds: [],
        invalidCartItemIds: [],
    };
}

export const useMenuAvailabilityQuery = () => {
    return useQuery({
        queryKey: ['menu', 'availability'],
        queryFn: async () => {
            const { data } = await api.get(API.MENU_AVAILABILITY);
            return (data?.days ?? []) as AvailabilityDay[];
        },
    });
};

export interface MenuSlotResponse {
    slot: ResolvedSlot | null;
    foodUpdates: Map<number, FoodUpdate>;
    availableMenuIds: number[];
    invalidCartItemIds: number[];
}

export const useMenuSlotQuery = (date?: string | null, cartOrders?: Array<{ date?: string; period?: string; fulfilment?: string; items?: Array<{ id: number; quantity: number }> }>) => {
    return useQuery({
        // Include date and cartOrders in query key so it refetches automatically
        queryKey: ['menu', 'slot', date, cartOrders],
        queryFn: async (): Promise<MenuSlotResponse> => {
            const body: Record<string, unknown> = {};
            if (cartOrders) body.cartOrders = cartOrders;

            let queryDate = date;
            if (!queryDate) {
                const stored = localStorage.getItem('selectedServiceDate');
                if (stored) {
                    queryDate = stored;
                } else {
                    const today = new Date();
                    queryDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                }
            }
            body.date = queryDate;

            try {
                const { data } = await api.post(API.MENU, body);

                const slotData = data.slot as ResolvedSlot | undefined;
                const updates: FoodUpdate[] = data.food_updates ?? [];

                const updateMap = new Map<number, FoodUpdate>();
                for (const u of updates) {
                    updateMap.set(u.id, u);
                }

                return {
                    slot: slotData
                        ? {
                              date: slotData.date,
                              period: slotData.period,
                              fulfilment: slotData.fulfilment,
                              slotWeight: slotData.slotWeight ?? 0,
                          }
                        : null,
                    foodUpdates: updateMap,
                    availableMenuIds: data.menu_updates ?? [],
                    invalidCartItemIds: data.invalid_cart_item_ids ?? [],
                };
            } catch {
                return buildFallback(queryDate ?? undefined);
            }
        },
    });
};
